import { useCallback, useEffect, useRef, useState } from 'react';

import {
  configureAuthorization,
  initializeBasketHttp,
  SfccSettings,
  SiteContext,
} from '@gn-audio/jss-core';

import { FormatSettings, getCurrencyFormats } from 'api/pricesApi/formattingApi';
import {
  configureMavenoidApi,
  configurePricingApi,
  initializeHttpClients,
} from 'common/core-components/bootstrapApis';
import { JabraPageProps } from 'common/pages/pagesTypes';

type AppBootstrapProps = Pick<JabraPageProps, 'envConfig' | 'siteSettings'> & {
  language: string;
  siteContext: SiteContext;
};

interface UseAppBootstrapResult {
  formatSettings?: FormatSettings;
  authReady: boolean;
}

export const useAppBootstrap = ({
  envConfig,
  siteSettings,
  language,
  siteContext,
}: AppBootstrapProps): UseAppBootstrapResult => {
  const [formatSettings, setFormatSettings] = useState<FormatSettings | undefined>(undefined);
  const [authReady, setAuthReady] = useState<boolean>(false);

  const initializeHttpClientsRef = useRef(false);
  if (!initializeHttpClientsRef.current && envConfig && siteContext) {
    try {
      initializeHttpClients(envConfig, siteContext);
      initializeHttpClientsRef.current = true;
    } catch (error) {
      console.error('Error initializing HTTP clients:', error);
    }
  }

  const pricingConfigRef = useRef<{ sfcc: SfccSettings } | null>(null);
  const authBootstrapRef = useRef(false);

  if (!authBootstrapRef.current && siteSettings?.authorization) {
    try {
      configureAuthorization(siteSettings, siteContext, language);
      configureMavenoidApi(siteSettings.authorization);
      authBootstrapRef.current = true;
      setAuthReady(true);
    } catch (error) {
      console.error('Error configuring authorization:', error);
    }
  }
  if (siteSettings?.sfcc && pricingConfigRef.current?.sfcc !== siteSettings.sfcc) {
    try {
      configurePricingApi(siteSettings.sfcc, true);
      pricingConfigRef.current = { sfcc: siteSettings.sfcc };
    } catch (error) {
      console.error('Error configuring pricing API:', error);
    }
  }

  const runClientBootstrap = useCallback(async () => {
    if (!envConfig || !siteSettings?.sfcc) return;
    try {
      await initializeBasketHttp(siteSettings.sfcc);
      const formatSettings = await getCurrencyFormats(siteSettings, language);
      setFormatSettings(formatSettings);
    } catch (error) {
      console.error('Error bootstrapping client-side components:', error);
    }
  }, [siteSettings, envConfig, language]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    void runClientBootstrap();
  }, [runClientBootstrap]);

  return { formatSettings, authReady };
};

export default useAppBootstrap;
